import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../context/AuthContext';

export interface Invoice {
    id: string;
    file_name: string;
    uploaded_at: string;
    status: string;
    company_id: string;

    // Extracted Data - supports both legacy (nested) and Document AI (flat) formats
    extracted_data?: {
        // Legacy nested format
        vendor?: { name?: string; tax_id?: string; address?: string };
        financials?: { total_amount?: number; currency?: string; tax?: number; subtotal?: number };
        line_items?: any[];
        field_locations?: any[];

        // Document AI flat format
        supplier_name?: string;
        supplier_tax_id?: string;
        supplier_address?: string;
        total_amount?: number;
        total_tax_amount?: number;
        net_amount?: number;
        currency?: string;
        items?: any[];
        entities_with_bounds?: any[];

        // Common fields
        invoice_number?: string;
        invoice_date?: string;
        due_date?: string;

        // Allow additional fields
        [key: string]: any;
    };

    // Comparison / Match Data
    comparison?: {
        status?: string; // matched, discrepancy, pending_review
        difference?: number;
        expected_amount?: number; // From Quote
        actual_amount?: number;   // From Invoice
        linked_quote_id?: string;
        match_score?: number;
        verification_result?: any; // Full GPT output
    };
}

interface UseInvoiceDataReturn {
    data: Invoice[];
    isLoading: boolean;
    error: string | null;
    pagination: {
        page: number;
        limit: number;
        total: number;
        pages: number;
        has_next: boolean;
        has_prev: boolean;
    };
    fetchData: (params: any) => void;
}

export const useInvoiceData = (initialMatchStatus?: string): UseInvoiceDataReturn => {
    const { token } = useAuth();
    const [data, setData] = useState<Invoice[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        pages: 0,
        has_next: false,
        has_prev: false
    });

    const fetchData = useCallback(async (params: any = {}) => {
        if (!token) return;

        setIsLoading(true);
        setError(null);

        try {
            const queryParams = new URLSearchParams();
            queryParams.append('page', (params.page || 1).toString());
            queryParams.append('page_size', (params.limit || 20).toString());

            // Match status filter (matched, unmatched, discrepancy)
            if (params.matchStatus) {
                queryParams.append('match_status', params.matchStatus);
            }

            const response = await fetch(`/api/invoices?${queryParams.toString()}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Failed to fetch invoices');
            }

            const result = await response.json();

            if (result.success) {
                const { invoices, total, page, page_size, has_next } = result.data;
                setData(invoices);
                setPagination({
                    page,
                    limit: page_size,
                    total,
                    pages: Math.ceil(total / page_size),
                    has_next,
                    has_prev: page > 1
                });
            } else {
                setError(result.message || 'Error fetching data');
            }
        } catch (err: any) {
            setError(err.message || 'Network error');
        } finally {
            setIsLoading(false);
        }
    }, [token]);

    // Initial fetch (includes filter if provided via URL param)
    useEffect(() => {
        fetchData({ matchStatus: initialMatchStatus || undefined });
    }, [fetchData, initialMatchStatus]);

    return { data, isLoading, error, pagination, fetchData };
};
